import React from 'react';
import Router from 'next/router';
import styled from 'styled-components';
import { Trans } from '@lingui/react';
import { Box, Flex } from '@components/layout/Grid';
import { NextDayTitle } from '@components/texts';
import Icon from '@components/Icon';
import { ProgramCategory } from '@gql/generated';
import SvgSurveyIcon from '@assets/SurveyIcon';

const SmallSurveyIcon = styled(SvgSurveyIcon)`
  height: 16px;
  width: 16px;

  path {
    fill: white;
  }
`;

const Wrapper = styled.div`
  display: flex;
`;

const SurveyIcon = styled(SvgSurveyIcon)<{ category: ProgramCategory }>`
  position: absolute;
  left: 0;
  bottom: -10px;
  width: 60px;
  height: 60px;

  path {
    fill: rgba(255, 255, 255, 0.1);
  }
`;

const ThumbWrapper = styled(Box)<{ category: ProgramCategory }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ isHighlighted, theme, category }) =>
    isHighlighted
      ? `border: 2px solid ${theme.helpers.getProgramThemeColor(category)}};`
      : ''}
  border-radius: ${({ isHighlighted }) => (isHighlighted ? '6px' : '4px')};
  background-color: ${({ theme, category }) =>
    theme.helpers.getProgramThemeColor(category)}};
  overflow: hidden;

  :before {
    content: "";
    display: block;
    width: 100%;
    padding-top: 56.25%;
  }
`;

const Document = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  color: white;
  text-transform: capitalize;
  transform: translate(-50%, -50%);

  svg {
    margin-right: 6px;
  }
`;

const VideoTag = styled.span`
  display: inline-flex;
  align-items: center;
  color: #242424;
  font-size: 12px;
  margin-right: 5px;

  & + & {
    margin-left: 5px;
  }

  svg {
    width: 11px;
    height: 11px;
    margin-right: 6px;

    path {
      fill: #242424;
    }
  }
`;

type Props = {
  title: string;
  onClick(): void;
  videoId: string;
  lang: string;
  locked: boolean;
  isHighlighted: boolean;
  programCategory: ProgramCategory;
  tags?: string[];
  redirectTo?: {
    programId: string;
    phaseId: string;
    stepId: string;
  };
};
const SurveyElementBox = (props: Props) => {
  return (
    <Wrapper
      // DO NOT REMOVE THE CLASS SurveyBox, ITS USED TO DRAW THE PROGRESS INDICATOR
      className="SurveyBox"
      onClick={() => {
        if (typeof props.onClick === 'function') {
          props.onClick();
        } else if (props.locked) {
          Router.push(
            `/${props.lang}/${props.programCategory}/${props.redirectTo.programId}/${props.redirectTo.phaseId}/${props.redirectTo.stepId}/${props.videoId}`,
            null,
            { scroll: false }
          );
        }
      }}
      style={{ cursor: 'pointer' }}
      // DO NOT REMOVE THE DATA ATTRIBUTES, THEY ARE USED TO DRAW THE PROGRESS INDICATOR
      data-video-finished={props?.['meta-data']?.finished === true}
      data-video-bonus={props?.['meta-data']?.bonus}
      data-video-last={props?.['meta-data']?.last}
      data-testid="SurveyBox"
    >
      <ThumbWrapper
        flex="0 0 108px"
        mr="10px"
        isHighlighted={props.isHighlighted}
        category={props.programCategory}
      >
        <SurveyIcon category={props.programCategory} />
        <Document>
          {props.locked ? (
            <Icon
              name="ygb-icon-programlock-ic"
              color="white"
              fontSize="14px"
              style={{ zIndex: 1 }}
            />
          ) : (
            <>
              <SmallSurveyIcon />
              <Trans id="metadata.survey" />
            </>
          )}
        </Document>
      </ThumbWrapper>
      <Flex flexDirection="column" justifyContent="center">
        <NextDayTitle
          isCurrent={props.isHighlighted}
          title={props.title}
          data-testid="SurveyBox--Title"
        >
          {props.title}
        </NextDayTitle>
        <VideoTag>
          <SmallSurveyIcon />
          <Trans id="metadata.survey" />
        </VideoTag>
        {props.tags?.length > 0 ? (
          <Flex alignItems="center">
            {(props.tags || []).map(tag => {
              if (typeof tag === 'string') {
                return (
                  <>
                    {' - '}
                    <VideoTag key={tag}>
                      <Trans id={tag} />
                    </VideoTag>
                  </>
                );
              }

              return tag;
            })}
          </Flex>
        ) : null}
      </Flex>
    </Wrapper>
  );
};

export default SurveyElementBox;
